import { useEffect, useContext } from 'react';
import { AuthContext } from '../App';

const DesignSystemProvider = ({ children }) => {
  const { API } = useContext(AuthContext);

  useEffect(() => {
    const loadDesignSystemCSS = async () => {
      try {
        const response = await fetch(`${API}/design-system/css`);
        const data = await response.json();

        // Reuse the existing <style> tag so we don't get a flash of unstyled
        // content each poll. Only touch the DOM when the CSS actually changed.
        let styleElement = document.getElementById('design-system-styles');
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = 'design-system-styles';
          document.head.appendChild(styleElement);
        }
        if (styleElement.textContent !== data.css) {
          styleElement.textContent = data.css;
        }
      } catch (error) {
        // Silent fail — index.css already provides a working set of CSS variables.
        if (process.env.NODE_ENV === 'development') {
          console.warn('Design system CSS not loaded — using bundled defaults.', error);
        }
      }
    };

    // Load CSS immediately
    loadDesignSystemCSS();

    // Set up polling to check for design system changes every 30 seconds
    const interval = setInterval(loadDesignSystemCSS, 30000);

    // Also reload when theme preference changes in localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        loadDesignSystemCSS();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [API]);

  // Initialize theme + accent from localStorage on mount so the user's
  // choice from the ThemePicker (Community page) persists across the app.
  useEffect(() => {
    const root = document.documentElement;

    const savedTheme = localStorage.getItem('theme') || 'light';
    if (savedTheme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');

    const ACCENTS = {
      amber:   '217 87% 55%',   /* re-mapped to logo electric blue */
      blue:    '217 87% 55%',
      emerald: '158 74% 40%',
      violet:  '265 84% 60%',
    };
    // Force-migrate legacy amber default to blue (post-rebrand).
    let savedAccent = localStorage.getItem('accent') || 'blue';
    if (savedAccent === 'amber') {
      savedAccent = 'blue';
      try { localStorage.setItem('accent', 'blue'); } catch { /* ignore */ }
    }
    const hsl = ACCENTS[savedAccent];
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
    }
  }, []);

  return children;
};

export default DesignSystemProvider;
